// Mock beneficiaries module (spec §4–6, §15): list, single-call create/update,
// delete, UPI/mobile verification and the account-name lookup.
import type {
  AccountStatusInfo,
  Beneficiary,
  BeneficiaryAccount,
  CreateBeneficiaryInput,
  NewAccountInput,
  UpdateBeneficiaryInput,
  VerifyResult,
} from '@/types/phase2.types';
import { LOOKUP_NAMES } from '../data';
import { loadDb, saveDb } from '../db';
import { mockNow } from '../clock';
import { fail, mockId, pause, pickIndex } from '../util';

const COOLING_MS = 30 * 60_000;
const CAP_WINDOW_MS = 24 * 3_600_000;

/** Status the server stamps on a newly added account (§14.1, §17.6). */
const initialStatus = (account: NewAccountInput, now: number): AccountStatusInfo => {
  if (account.kind === 'MOBILE_TRANSFER') return { status: 'ACTIVE' };
  if (account.kind === 'INTERNATIONAL') return { status: 'ACTIVATING', activeAt: now + COOLING_MS };
  return { status: 'ACTIVATING', activeAt: now + COOLING_MS, limitedUntil: now + CAP_WINDOW_MS };
};

/** Recomputes statuses from the mock clock on every read — the client never
 *  works status out from the device clock (§4.1). */
const refreshStatus = (account: BeneficiaryAccount, now: number, skipCooling: boolean): void => {
  if (account.kind === 'MOBILE_TRANSFER' || skipCooling) {
    account.status = { status: 'ACTIVE' };
    return;
  }
  const { activeAt, limitedUntil } = account.status;
  if (activeAt && now < activeAt) {
    account.status = { ...account.status, status: 'ACTIVATING' };
  } else if (account.kind === 'LOCAL_TRANSFER' && limitedUntil && now < limitedUntil) {
    account.status = { ...account.status, status: 'LIMITED' };
  } else {
    account.status = { status: 'ACTIVE' };
  }
};

const aliasTaken = (db: ReturnType<typeof loadDb>, alias: string, exceptId?: string): boolean =>
  db.beneficiaries.some(
    (b) => b.id !== exceptId && b.alias.trim().toLowerCase() === alias.trim().toLowerCase()
  );

export const listBeneficiaries = async (): Promise<Beneficiary[]> => {
  await pause(400);
  const db = loadDb();
  const now = mockNow(db);
  db.beneficiaries.forEach((b) => b.accounts.forEach((a) => refreshStatus(a, now, db.limits.skipCooling)));
  saveDb(db);
  return db.beneficiaries;
};

export const createBeneficiary = async (input: CreateBeneficiaryInput): Promise<Beneficiary> => {
  await pause(600);
  const db = loadDb();
  const alias = input.alias.trim();
  if (aliasTaken(db, alias)) {
    fail('DUPLICATE_ALIAS', 'You already have a beneficiary with this name.');
  }
  if (input.accounts.length === 0 || input.accounts.length > 5) {
    fail('BAD_ACCOUNTS', 'A beneficiary needs between 1 and 5 accounts.');
  }
  const now = mockNow(db);
  const beneficiary: Beneficiary = {
    id: mockId('ben'),
    alias,
    accounts: input.accounts.map((a) => ({
      ...a,
      id: mockId('acc'),
      status: initialStatus(a, now),
    })) as BeneficiaryAccount[],
    createdAt: now,
    updatedAt: now,
  };
  db.beneficiaries.push(beneficiary);
  saveDb(db);
  return beneficiary;
};

export const updateBeneficiary = async (
  id: string,
  input: UpdateBeneficiaryInput
): Promise<Beneficiary> => {
  await pause(600);
  const db = loadDb();
  const beneficiary = db.beneficiaries.find((b) => b.id === id) ?? fail('NOT_FOUND', 'Beneficiary not found.');
  const alias = input.alias.trim();
  if (aliasTaken(db, alias, id)) {
    fail('DUPLICATE_ALIAS', 'You already have a beneficiary with this name.');
  }
  const kept = beneficiary.accounts.filter((a) => input.keepAccountIds.includes(a.id));
  const now = mockNow(db);
  const added = input.newAccounts.map((a) => ({
    ...a,
    id: mockId('acc'),
    status: initialStatus(a, now),
  })) as BeneficiaryAccount[];
  const accounts = [...kept, ...added];
  if (accounts.length === 0 || accounts.length > 5) {
    fail('BAD_ACCOUNTS', 'A beneficiary needs between 1 and 5 accounts.');
  }
  beneficiary.alias = alias;
  beneficiary.accounts = accounts;
  beneficiary.updatedAt = now;
  saveDb(db);
  return beneficiary;
};

export const deleteBeneficiary = async (id: string): Promise<{ alias: string }> => {
  await pause(500);
  const db = loadDb();
  const idx = db.beneficiaries.findIndex((b) => b.id === id);
  if (idx < 0) fail('NOT_FOUND', 'Beneficiary not found.');
  const [{ alias }] = db.beneficiaries.splice(idx, 1);
  saveDb(db);
  return { alias };
};

// ---------- Lookups (spec §15) ----------

export const verifyUpiId = async (upiId: string): Promise<VerifyResult> => {
  await pause(500);
  const normalised = upiId.trim().toLowerCase();
  if (normalised.endsWith('@invalid')) return { outcome: 'NOT_FOUND' };
  const name = normalised.split('@')[0].toUpperCase();
  if (normalised.includes('fraud')) return { outcome: 'FLAGGED', holderName: name };
  return { outcome: 'FOUND', holderName: name };
};

export const verifyMobile = async (mobile: string): Promise<VerifyResult> => {
  await pause(500);
  if (mobile.endsWith('0000')) return { outcome: 'NOT_FOUND' };
  const lastTwo = parseInt(mobile.slice(-2), 10);
  return { outcome: 'FOUND', holderName: LOOKUP_NAMES[lastTwo % LOOKUP_NAMES.length] };
};

export const lookupAccountName = async (
  _ifsc: string,
  accountNumber: string
): Promise<VerifyResult> => {
  await pause(700);
  if (accountNumber.endsWith('1111')) return { outcome: 'FOUND', holderName: 'RAHUL SHARMA' };
  if (accountNumber.endsWith('2222')) return { outcome: 'FOUND', holderName: 'PRIYA NAIR' };
  if (accountNumber.endsWith('0000')) return { outcome: 'NOT_FOUND' };
  return { outcome: 'FOUND', holderName: LOOKUP_NAMES[pickIndex(accountNumber, LOOKUP_NAMES.length)] };
};
